import { FC, useEffect, useMemo, useState } from 'react';
import { Modal, Loader } from '../../../../components';
import { AlertSVG } from '../../../../components/svgs';
import { ParsedExcelSheet, ParsedExcelWorkbook, revokeParsedExcelLogos } from '../../../../utils/parseExcelFile';
import {
  BulkDepositHighlightedCell,
  BulkDepositValidationError,
  getHighlightedCellKey,
  mapValidationErrorsToCells,
} from '../../../../utils/parseBulkDepositValidation';
import { BUTTON_CONSTANTS, LABEL_CONSTANTS } from '../../../../constants/common';

type BulkDepositImportPreviewProps = {
  isOpen: boolean;
  fileName: string;
  workbook: ParsedExcelWorkbook | null;
  isImporting: boolean;
  validationErrors: BulkDepositValidationError[];
  validationMessage: string;
  onClose: () => void;
  onConfirmImport: () => void;
};

const BulkDepositImportPreview: FC<BulkDepositImportPreviewProps> = ({
  isOpen,
  fileName,
  workbook,
  isImporting,
  validationErrors,
  validationMessage,
  onClose,
  onConfirmImport,
}) => {
  const sheets = workbook?.sheets ?? [];
  const hasData = sheets.some(sheet => sheet.headers.length > 0);
  const canImport = hasData && !isImporting && sheets.some(sheet => sheet.rows.length > 0);
  const brandingSheet =
    sheets.find(sheet => sheet.companyInfo.length > 0 || Boolean(sheet.website)) ??
    sheets.find(sheet => sheet.metadata.length > 0 || sheet.notes.length > 0) ??
    sheets[0];
  const [activeSheetName, setActiveSheetName] = useState<string>('');

  const highlightedCells = useMemo(
    () => mapValidationErrorsToCells(workbook, validationErrors),
    [workbook, validationErrors],
  );

  const highlightedCellMessages = useMemo(() => {
    const messagesByCell = new Map<string, string[]>();

    highlightedCells.forEach(cell => {
      const key = getHighlightedCellKey(cell.sheetName, cell.rowIndex, cell.columnIndex);
      const messages = messagesByCell.get(key) ?? [];
      if (!messages.includes(cell.message)) {
        messages.push(cell.message);
      }
      messagesByCell.set(key, messages);
    });

    return messagesByCell;
  }, [highlightedCells]);

  const highlightedCellByErrorIndex = useMemo(() => {
    const firstCellByErrorIndex = new Map<number, BulkDepositHighlightedCell>();
    highlightedCells.forEach(cell => {
      if (!firstCellByErrorIndex.has(cell.errorIndex)) {
        firstCellByErrorIndex.set(cell.errorIndex, cell);
      }
    });
    return firstCellByErrorIndex;
  }, [highlightedCells]);

  const errorCountBySheet = useMemo(() => {
    const counts = new Map<string, number>();
    highlightedCells.forEach(cell => {
      counts.set(cell.sheetName, (counts.get(cell.sheetName) ?? 0) + 1);
    });
    return counts;
  }, [highlightedCells]);

  useEffect(() => {
    return () => {
      revokeParsedExcelLogos(workbook);
    };
  }, [workbook]);

  useEffect(() => {
    if (!sheets.length) {
      setActiveSheetName('');
      return;
    }

    const stillExists = sheets.some(sheet => sheet.name === activeSheetName);
    if (!stillExists) {
      setActiveSheetName(sheets[0].name);
    }
  }, [sheets, activeSheetName]);

  useEffect(() => {
    const firstErrorSheet = highlightedCells[0]?.sheetName ?? validationErrors.find(error => error.sheetName)?.sheetName;
    if (!firstErrorSheet) {
      return;
    }

    const matchingSheet = sheets.find(
      sheet => sheet.name === firstErrorSheet || sheet.name.toLowerCase().includes(firstErrorSheet.toLowerCase()),
    );
    if (matchingSheet) {
      setActiveSheetName(matchingSheet.name);
    }
  }, [highlightedCells, sheets, validationErrors]);

  useEffect(() => {
    if (!highlightedCells.length) {
      return;
    }

    const firstCell = highlightedCells[0];
    const cellElement = document.getElementById(
      getHighlightedCellKey(firstCell.sheetName, firstCell.rowIndex, firstCell.columnIndex),
    );
    cellElement?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [highlightedCells, activeSheetName]);

  const activeSheet = sheets.find(sheet => sheet.name === activeSheetName) ?? sheets[0];
  const metadataSheet = activeSheet ?? brandingSheet;
  const showValidationBanner = !isImporting && (validationErrors.length > 0 || Boolean(validationMessage));

  const getCellMessages = (sheetName: string, rowIndex: number, columnIndex: number) =>
    highlightedCellMessages.get(getHighlightedCellKey(sheetName, rowIndex, columnIndex)) ?? [];

  const isErrorHeader = (sheetName: string, columnIndex: number) =>
    highlightedCells.some(cell => cell.sheetName === sheetName && cell.columnIndex === columnIndex);

  const isErrorRow = (sheetName: string, rowIndex: number) =>
    highlightedCells.some(cell => cell.sheetName === sheetName && cell.rowIndex === rowIndex);

  const handleValidationErrorClick = (cell: BulkDepositHighlightedCell) => {
    setActiveSheetName(cell.sheetName);
    requestAnimationFrame(() => {
      document.getElementById(getHighlightedCellKey(cell.sheetName, cell.rowIndex, cell.columnIndex))?.scrollIntoView({
        block: 'nearest',
        inline: 'nearest',
      });
    });
  };

  const renderSheetTable = (sheet: ParsedExcelSheet) => (
    <div className="preview-table-wrapper" key={`${sheet.name}-table`}>
      <table>
        <thead>
          <tr>
            {sheet.headers.map((header, index) => (
              <th
                key={`${sheet.name}-header-${header}-${index}`}
                className={isErrorHeader(sheet.name, index) ? 'error-header' : undefined}>
                {header || `Column ${index + 1}`}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sheet.rows.length > 0 ? (
            sheet.rows.map((row, rowIndex) => (
              <tr
                key={`${sheet.name}-row-${row.join('|')}-${rowIndex}`}
                className={isErrorRow(sheet.name, rowIndex) ? 'error-row' : undefined}>
                {row.map((cell, cellIndex) => {
                  const cellMessages = getCellMessages(sheet.name, rowIndex, cellIndex);
                  const cellKey = getHighlightedCellKey(sheet.name, rowIndex, cellIndex);
                  return (
                    <td
                      id={cellMessages.length ? cellKey : undefined}
                      key={`${sheet.name}-cell-${rowIndex}-${sheet.headers[cellIndex] ?? cellIndex}`}
                      className={cellMessages.length ? 'error-cell' : undefined}
                      title={cellMessages.join('\n')}>
                      {cell ?? ''}
                    </td>
                  );
                })}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={Math.max(sheet.headers.length, 1)} className="empty-sheet-message">
                {LABEL_CONSTANTS.NO_RESULTS_FOUND}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} width={1100} ignoreBackdrop>
      <div className="bulk-deposit-import-preview">
        <div className="header">
          <h4>{LABEL_CONSTANTS.BULK_DEPOSIT_PREVIEW}</h4>
          <p className="file-name">{fileName}</p>
        </div>
        <div className="body">
          {isImporting ? (
            <div className="loading-state">
              <Loader loaderSize="small" />
              <p>{LABEL_CONSTANTS.IMPORTING_BULK_DEPOSIT}</p>
            </div>
          ) : !hasData ? (
            <p className="empty-state">{LABEL_CONSTANTS.NO_RESULTS_FOUND}</p>
          ) : (
            <div className="preview-content">
              {showValidationBanner && (
                <div className="preview-validation-errors">
                  <div className="validation-title">
                    <AlertSVG />
                    <span>{LABEL_CONSTANTS.BULK_DEPOSIT_VALIDATION_TITLE}</span>
                  </div>
                  {validationErrors.length > 0 ? (
                    <ul>
                      {validationErrors.map((error, index) => {
                        const highlightedCell = highlightedCellByErrorIndex.get(index);
                        const errorSheetName = error.sheetName;
                        return (
                          <li key={`${error.depositId ?? 'file'}-${error.field ?? 'message'}-${error.rowNumber ?? index}`}>
                            {highlightedCell || errorSheetName ? (
                              <button
                                type="button"
                                className="validation-error-link"
                                onClick={() => {
                                  if (highlightedCell) {
                                    handleValidationErrorClick(highlightedCell);
                                    return;
                                  }
                                  if (errorSheetName) {
                                    setActiveSheetName(errorSheetName);
                                  }
                                }}>
                                {error.message}
                              </button>
                            ) : (
                              error.message
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <p>{validationMessage}</p>
                  )}
                  <p className="validation-hint">{LABEL_CONSTANTS.BULK_DEPOSIT_VALIDATION_HINT}</p>
                </div>
              )}

              <div className="preview-branding">
                <div className="brand-left">
                  {workbook!.logos[0] && <img src={workbook!.logos[0]} alt="RSG logo" className="preview-logo" />}
                  {brandingSheet?.website && <p className="website">{brandingSheet.website}</p>}
                </div>
                {brandingSheet && brandingSheet.companyInfo.length > 0 && (
                  <div className="brand-right">
                    {brandingSheet.companyInfo.map(line => (
                      <p key={line}>{line}</p>
                    ))}
                  </div>
                )}
              </div>

              {metadataSheet && (metadataSheet.metadata.length > 0 || metadataSheet.notes.length > 0) && (
                <div className="preview-metadata">
                  {metadataSheet.metadata.map(item => (
                    <div className="metadata-row" key={`${metadataSheet.name}-${item.label}`}>
                      <span className="label">{item.label}:</span>
                      <span className="value">{item.value}</span>
                    </div>
                  ))}
                  {metadataSheet.notes.map(item => (
                    <div className="metadata-row note-row" key={`${metadataSheet.name}-${item.label}`}>
                      <span className="label">{item.label}:</span>
                      <span className="value note-value">{item.value}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="preview-separator" />

              {sheets.length > 1 && (
                <div className="preview-sheet-tabs" role="tablist" aria-label="Workbook sheets">
                  {sheets.map(sheet => (
                    <button
                      key={sheet.name}
                      type="button"
                      role="tab"
                      aria-selected={sheet.name === activeSheet?.name}
                      className={`preview-sheet-tab ${sheet.name === activeSheet?.name ? 'active' : ''} ${
                        errorCountBySheet.get(sheet.name) ? 'has-error' : ''
                      }`}
                      onClick={() => setActiveSheetName(sheet.name)}>
                      {sheet.name}
                      <span className="row-count">{sheet.rows.length}</span>
                    </button>
                  ))}
                </div>
              )}

              {activeSheet && (
                <div className="preview-sheet" key={activeSheet.name}>
                  {sheets.length === 1 && <h5 className="sheet-title">{activeSheet.name}</h5>}
                  {renderSheetTable(activeSheet)}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="footer">
          <button type="button" className="button blue inverted small" onClick={onClose} disabled={isImporting}>
            {BUTTON_CONSTANTS.CANCEL}
          </button>
          <button
            type="button"
            className={canImport ? 'button green small' : 'button green small disabled'}
            onClick={onConfirmImport}
            disabled={!canImport}>
            {BUTTON_CONSTANTS.IMPORT}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default BulkDepositImportPreview;
